import type { List } from 'immutable'
import { Map, fromJS } from 'immutable'
import { identity } from 'lodash-es'

import type { PresetColor } from '../color-refs'
import { getContrastTextColor } from '../contrast-text-color'
import { themeColorNameKey } from '../../../utils/themes'
import { useThemeSettings } from './useThemeSettings'
import { useTranslate } from '../translate'

type ColorOption = Map<'value' | 'name' | 'ref', string>
type FormatColorFunction = (color: string) => string

export const isColorDefined = (value: unknown): value is string =>
  typeof value === 'string' && value !== '' && value !== 'transparent'

const constantColorEntries = [
  { value: '#000000', name: 'blackColor' },
  { value: '#ffffff', name: 'whiteColor' },
]
// Refs of the neutral presets; ColorPickerPanel groups these as monochrome.
export const constantColorRefs: readonly string[] = constantColorEntries.map((entry) => entry.name)
const constantColors: List<ColorOption> = fromJS(constantColorEntries)

type ColorOptionsConfig = {
  formatConstantColorName?: FormatColorFunction
  formatThemeColorName?: FormatColorFunction
  /**
   * Appends the theme's advanced Button color as ref 'button' when the theme
   * resolves one. Only banner button-color fields may opt in; the token has
   * no legacy index encoding, and appending it last keeps the existing option
   * positions stable.
   */
  includeButtonColor?: boolean
}

type ColorNameFormatters = Required<Pick<ColorOptionsConfig, 'formatConstantColorName' | 'formatThemeColorName'>>

/**
 * Display-name formatters for color pickers. Opt-in, because the names double as
 * `data-*-color` attribute values (see `useImageColorSettings`) that theme CSS
 * matches — those must stay untranslated. Translating unscoped keeps the result
 * independent of the caller's own translation scope.
 */
export function useColorNameFormatters(): ColorNameFormatters {
  const t = useTranslate('interface')

  return {
    formatConstantColorName: (name) => t(`components.colorFieldComponent.popover.colorPresets.${name}.tooltip`),
    formatThemeColorName: (name) => t(`themes:settings.${themeColorNameKey(name)}`),
  }
}

export function useColorOptions({
  formatConstantColorName = identity,
  formatThemeColorName = identity,
  includeButtonColor = false,
}: ColorOptionsConfig = {}): List<ColorOption> {
  const allThemeColors = useThemeSettings(true).get('colors', Map<string, string>()) as Map<string, string>

  const themeColors = allThemeColors
    // Cherry-pick only the base colors (color1, color2, ...)
    .filter((_value: string, key: string) => /^color\d+$/.test(key))
    // Merchants can remove the value of color4 and color5
    .filter(isColorDefined) as Map<string, string>

  const themeColorOptions = themeColors.map<ColorOption>(
    (value: string, name: string) =>
      Map({ value, name: formatThemeColorName(name), ref: `colors.${name}` }) as ColorOption,
  )

  const constantColorOptions = constantColors.map<ColorOption>(
    (color: ColorOption) =>
      Map({
        value: color.get('value'),
        name: formatConstantColorName(color.get('name')),
        ref: color.get('name'),
      }) as ColorOption,
  )

  const options = constantColorOptions.concat(themeColorOptions).toList()

  const buttonColor = allThemeColors.get('button')
  // Not every theme resolves a button color — horizon declares the variant
  // but ships no default for it.
  if (!includeButtonColor || !isColorDefined(buttonColor)) return options
  // The label key is defined beside the black/white preset keys, hence the constant formatter.
  return options.push(
    Map({
      value: buttonColor,
      name: formatConstantColorName('buttonColor'),
      ref: 'button',
    }) as ColorOption,
  )
}

/**
 * A stored ref takes precedence over the legacy positional index; a ref not
 * among the options (deleted color) falls back to the default index, never to
 * the stale legacy index
 */
export function resolveColorOptionIndex(
  colorOptions: List<ColorOption>,
  colorRef: string | null | undefined,
  legacyIndex: number | undefined,
  defaultIndex: number,
): number | undefined {
  if (colorRef == null) return legacyIndex
  const refIndex = colorOptions.findIndex((option) => option?.get('ref') === colorRef)
  return refIndex >= 0 ? refIndex : defaultIndex
}

/**
 * Resolves a banner color field's selected option from its ref, legacy index,
 * or field default.
 */
export function resolveColorOption(
  options: List<ColorOption>,
  source: ImmutableMap | undefined,
  field: string,
): ColorOption {
  const defaultIndex = imageColorTextSettingsDefaults.get(`${field}Index`)
  const index = resolveColorOptionIndex(options, source?.get(`${field}Ref`), source?.get(`${field}Index`), defaultIndex)
  return options.get(index ?? defaultIndex, options.get(defaultIndex))
}

/**
 * The banner option lists: only the button field resolves the 'button' ref,
 * so the plain list is the extended one without the button option.
 */
export function useBannerColorOptions(config: Omit<ColorOptionsConfig, 'includeButtonColor'> = {}): {
  colorOptions: List<ColorOption>
  buttonColorOptions: List<ColorOption>
} {
  const buttonColorOptions = useColorOptions({ ...config, includeButtonColor: true })
  return {
    colorOptions: buttonColorOptions.filter((option) => option?.get('ref') !== 'button').toList(),
    buttonColorOptions,
  }
}

/**
 * Typed conversion from the Immutable option list to the picker's PresetColor
 * shape, replacing untyped toJS() at the call sites.
 */
export function toPresetColors(options: List<ColorOption>): PresetColor[] {
  return options
    .toArray()
    .map((option) => ({ value: option.get('value'), name: option.get('name'), ref: option.get('ref') }))
}

export const imageColorTextSettingsDefaults = Map({
  textColorIndex: constantColors.findIndex((color) => color?.get('name') === 'whiteColor'),
  buttonColorIndex: constantColors.findIndex((color) => color?.get('name') === 'whiteColor'),
  backgroundColorIndex: constantColors.findIndex((color) => color?.get('name') === 'blackColor'),
  overlayOpacity: 40,
})

type ImageColorSettings = {
  textColor: ColorOption
  buttonColor: ColorOption
  backgroundColor: ColorOption
  overlayOpacity: number
}

export function useImageColorSettings(image: ImmutableMap): ImageColorSettings {
  const { colorOptions, buttonColorOptions } = useBannerColorOptions()

  const resolveOption = (field: string, options: List<ColorOption> = colorOptions): ColorOption => {
    const customColor = image.get(field)
    if (typeof customColor === 'string' && customColor) {
      return Map({ value: customColor, name: 'custom' }) as ColorOption
    }
    return resolveColorOption(options, image, field)
  }

  return {
    textColor: resolveOption('textColor'),
    buttonColor: resolveOption('buttonColor', buttonColorOptions),
    backgroundColor: resolveOption('backgroundColor'),
    overlayOpacity: image.get('overlayOpacity', imageColorTextSettingsDefaults.get('overlayOpacity')),
  }
}

/**
 * Inline style custom properties for custom (non-palette) banner colors.
 * Custom colors have no name the theme CSS could match; its
 * [data-*-color="custom"] rules read these vars instead.
 */
export function getCustomColorStyleVars({
  textColor,
  buttonColor,
  backgroundColor,
}: Pick<ImageColorSettings, 'textColor' | 'buttonColor' | 'backgroundColor'>) {
  const styleVars: Partial<
    Record<'--overlay-color' | '--text-color' | '--button-color' | '--button-contrast-color', string>
  > = {}

  if (backgroundColor.get('name') === 'custom') {
    styleVars['--overlay-color'] = backgroundColor.get('value')
  }
  if (textColor.get('name') === 'custom') {
    styleVars['--text-color'] = textColor.get('value')
  }
  if (buttonColor.get('name') === 'custom') {
    styleVars['--button-color'] = buttonColor.get('value')
    styleVars['--button-contrast-color'] = getContrastTextColor(buttonColor.get('value'))
  }

  return styleVars
}
